import { PaginatedResource } from '../../typings/Paginated';
import { AttachmentType, OccurrencePriorities } from '../../schemas/dispatch';
import { Log } from './audit-logs';
import { AlreadyExists, BaseService, ClientError, ExtendableError, GenericPaginatedParameters, InvalidValue, NotFound, PartialBy, Unauthorized, UnexpectedError } from './base-service';
import { EquipmentAlreadyInUse } from './deac';

export interface GetVehiclesPaginatedParameters extends GenericPaginatedParameters {
    status?: string;
}

export enum TeamNameCategory {
    LIGHT_VEHICLE = 'light_vehicle',
    MOTORCYCLE = 'motorcycle',
    ON_FOOT = 'on_foot',
    BICYCLE = 'bicycle',
    BOAT = 'boat',
    HEAVY_VEHICLE = 'heavy_vehicle',
    TRACTOR_TRUCK = 'tractor_truck',
    VAN = 'van',
    JET_SKI = 'jet_ski',
    BUS = 'bus'
}

interface CreateUnitParameters {
    unitNameId: number;
    unitProgramId: number;
    actingBodyUnitId: number;
    actingBodyCommandId: number;
    description?: string;
    usersToSet: number[];
    equipmentsToSet: number[];
    estimatedStart: string;
    estimatedFinish: string;
    /**
     * Modifications on agents assignments. The key stands for the AgentId and the value for the AssignmentId
     */
    modifiedAgentAssignments: { [agentId: number]: number; };
}

interface UpdateUnitParameters {
    unitId: number;
    unitNameId: number;
    unitProgramId: number;
    actingBodyCommandId: number;
    description?: string;
    usersToSet: number[];
    equipmentsToSet: number[];
    estimatedStart: string;
    estimatedFinish: string;
    /**
     * Modifications on agents assignments. The key stands for the AgentId and the value for the AssignmentId
     */
    modifiedAgentAssignments: { [agentId: number]: number; };
}

export interface GetUnitsPaginatedParameters extends GenericPaginatedParameters {
    agentFilter?: string;
    beginIn?: number;
    endIn?: number;
    status?: string[];
    actingBodyId?: number;
    actingBodyUnitIds?: number[];
    phone?: string;
    document?: string;
}

export class AlreadyWithThisUser extends ExtendableError {
    constructor() {
        super(`You selected the same user that already is the responsible for this occurrence.`);
    }
}

export class RecordNotLatestValues extends ExtendableError {
    userName: string;
    constructor(code: string, userName: string) {
        super(code);
        this.userName = userName;
    }
}

export class AlreadySignedReport extends ExtendableError {
    userName: string;
    constructor(code: string, userName: string) {
        super(code);
        this.userName = userName;
    }
}

interface CreateReportParameters {
    name: string;
    isPdf: boolean;
    isCsv: boolean;
    startDate: string;
    endDate: string;
    tags?: number[];
    actingBodyId?: number;
    assumedBy?: number;
    transferredBy?: number;
    receivedBy?: number;
    finishedBy?: number;
    actingBodyCommandIds?: number[];
    triggerTypeIds?: (TriggerType | 'manual')[];
    occurrenceTypesIds?: number[];
    occurrenceTagsIds?: number[];
    unitNamesIds?: number[];
    priority?: OccurrencePriorities;
    situation?: OccurrenceSituation;
    policeReport?: string;
    displaceReasonIds?: number[];
    actingBodyUnitIds?: number[];
    mapPolygonIds?: number[];
    latitude?: number;
    longitude?: number;
    radius?: number;
    withDispatch?: boolean;
    withoutDispatch?: boolean;
    withOccurrenceDocument?: boolean;
    withoutOccurrenceDocument?: boolean;
    cameraTags?: number[];
    finishReasonCategoryIds?: number[];
    finishReasonSubCategoryIds?: number[];
    attachmentTypes?: AttachmentType[];
    involvedPersonSearch?: string;
    handcuffUsed?: boolean;
    arrested?: boolean;
}

interface CreateTeamName extends Omit<BaseTeamName, 'id' | 'actingBodyId' | 'isConnected'> {
    actingBodyId?: number;
}

interface CreateAssignmentParameters extends Omit<BaseAssignment, 'id' | 'actingBodyId'> {
    actingBodyId?: number;
}

interface CreateUnitPauseReason extends Omit<BaseUnitPauseReason, 'id' | 'actingBodyId'> {
    actingBodyId?: number;
}

export interface GetResourcePaginatedByActingBodyParameters extends GenericPaginatedParameters {
    actingBodyId?: number;
    actingBodyCommandIds?: number[];
}

export interface GetOccurrenceTypesPaginatedParameters extends GetResourcePaginatedByActingBodyParameters {
    isActive: boolean;
}

export interface GetEquipmentsPaginatedParameters extends GetResourcePaginatedByActingBodyParameters {
    unitId?: number;
}

interface CreateOccurrenceType extends Omit<BaseOccurrenceType, 'id' | 'actingBodyId'> {
    actingBodyId?: number;
    tags?: number[];
}

export interface SimplifiedOccurrenceType extends Omit<BaseOccurrenceType, 'active' | 'actingBodyId'> { }

export enum HandcuffReasonType {
    RESISTANCE = 'RESISTANCE',
    FEAR_OF_ESCAPE = 'FEAR_OF_ESCAPE',
    DANGER_TO_PHYSICAL_INTEGRITY = 'DANGER_TO_PHYSICAL_INTEGRITY',
}

export const HandcuffReadableReasons = {
    NONE: null,
    [HandcuffReasonType.RESISTANCE]: 'resistance',
    [HandcuffReasonType.FEAR_OF_ESCAPE]: 'fearOfEscape',
    [HandcuffReasonType.DANGER_TO_PHYSICAL_INTEGRITY]: 'dangerToPhysicalIntegrity',
} as const;

export type HandcuffReasonReadableTypes = typeof HandcuffReadableReasons;

export interface HandcuffReason {
    id: number;
    name: string;
    type: HandcuffReasonType;
    actingBody: ActingBodySimplified;
}

export interface CreateHandcuffReasonParameters {
    id: number;
    name: string;
    type: string | null;
    actingBodyId?: number;
}

export interface GetOccurrencesParameters extends GenericPaginatedParameters {
    triggerType?: TriggerType | 'manual';
    priority?: OccurrencePriorities;
    situation?: OccurrenceSituation;
    occurrenceTypeId?: number[];
    responsibleUserId?: number;
    unitNamesIds?: number[];
    tags?: number[];
    actingBodyId?: number;
    assumedBy?: number;
    transferredBy?: number;
    receivedBy?: number;
    finishedBy?: number;
    actingBodyCommandIds?: number[];
    displaceReasonIds?: number[];
    latitude?: number;
    longitude?: number;
    radius?: number;
}

interface BulkFinishOccurrencesParams {
    actingBodyId: number;
    beginIn: number | undefined;
    endIn: number | undefined;
    triggerType: (TriggerType | 'manual');
    finishCategoryId: number | null;
    finishSubCategoryId: number | null;
}

export interface SimplifiedEquipments extends DropdownResource {
    equipmentCategoryId?: number;
    equipmentCategory?: {
        id: number;
        name: string;
    };
}

export const ONLINE_AGENT_TIME_MINUTES = 15;
export class DispatchService extends BaseService {
    async getOccurrences(params: GetOccurrencesParameters): Promise<PaginatedResource<PaginatedOccurrence>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrences${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getActiveUnits(): Promise<Unit[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/simplified-active-units`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getDropDownOccurrenceTypes(actingBodyId?: number): Promise<SimplifiedOccurrenceType[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/simplified-occurrence-types${this.encodeQueryParams({
            actingBodyId
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getDropDownUnitPrograms(actingBodyId?: number, includeDeleted?: boolean): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit-programs-simplified${this.encodeQueryParams({
            actingBodyId,
            includeDeleted
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getDropDownProvidences(actingBodyId?: number): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/providences-simplified${this.encodeQueryParams({
            actingBodyId
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getDropDownCityProperties(actingBodyId?: number): Promise<DropdownCityProperty[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/city-properties-simplified${this.encodeQueryParams({
            actingBodyId
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getHandcuffReasons(actingBodyId?: number): Promise<HandcuffReason[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/handcuff-reasons${this.encodeQueryParams({
            actingBodyId
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOccurrenceTypes(params: GetOccurrenceTypesPaginatedParameters): Promise<PaginatedResource<OccurrenceType>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence_types${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOccurrenceVideoUrl(id: number): Promise<VideoUrl> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence/${id}/video-url`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOccurrenceImageSasToken(occurrenceId: number, occurrenceImageId: number): Promise<OccurrenceImage> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence/${occurrenceId}/image-sas-token`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ occurrenceImageId })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getAgentPositions(unitName?: string, actingBodyId?: number): Promise<UnitMapPosition[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/agent/positions${this.encodeQueryParams({ unitName, actingBodyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOccurrenceAgentPositions(occurrenceId: number, unitName?: string): Promise<UnitMapPosition[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence/${occurrenceId}/agent/positions${this.encodeQueryParams({ unitName })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getEquipments(params: GetEquipmentsPaginatedParameters): Promise<PaginatedResource<Equipment>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/equipments${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createPersonRelation(personRelation: PartialBy<Omit<BasePersonRelation, 'id'>, 'name'>): Promise<PersonRelation> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/person-relation`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(personRelation)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updatePersonRelation(personRelation: PartialBy<Omit<BasePersonRelation, 'actingBodyId'>, 'id'>): Promise<PersonRelation> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/person-relation/${personRelation.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(personRelation)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPersonRelations(params: GetResourcePaginatedByActingBodyParameters): Promise<PaginatedResource<PersonRelation>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/person-relations${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedPersonRelations(actingBodyId: number): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/person-relations-simplified${this.encodeQueryParams({ actingBodyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deletePersonRelation(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/person-relation/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async createVehicleRelation(vehicleRelation: PartialBy<Omit<VehicleRelation, 'id'>, 'name'>): Promise<VehicleRelation> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/vehicle-relation`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(vehicleRelation)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateVehicleRelation(vehicleRelation: PartialBy<Omit<VehicleRelation, 'actingBodyId'>, 'id'>): Promise<VehicleRelation> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/vehicle-relation/${vehicleRelation.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(vehicleRelation)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getVehicleRelations(params: GetResourcePaginatedByActingBodyParameters): Promise<PaginatedResource<VehicleRelation>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/vehicle-relations${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedVehicleRelations(actingBodyId: number): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/vehicle-relations-simplified${this.encodeQueryParams({ actingBodyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteVehicleRelation(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/vehicle-relation/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getSimplifiedEquipments(actingBodyId: number): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/equipments-simplified${this.encodeQueryParams({ actingBodyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getUnitsSimplifiedEquipments(actingBodyId: number, unitId?: number): Promise<SimplifiedEquipments[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit-equipments-simplified${this.encodeQueryParams({ actingBodyId, unitId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getEquipmentCategorySimplified(actingBodyId: number): Promise<(DropdownResource & { available: number; })[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/equipments-categories-simplified${this.encodeQueryParams({ actingBodyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOccurrencePositions(actingBodyId?: number): Promise<OccurrencePosition[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrences/positions${this.encodeQueryParams({
            actingBodyId: actingBodyId
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getMapPolygonIds(): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/map-polygons`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status === 401) {
            throw new Unauthorized();
        }

        if (res.status !== 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getActingBodies(): Promise<ActingBodySimplified[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/acting-bodies`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getUnitNames(actingBodyId?: number, includeDeleted?: boolean): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit-names${this.encodeQueryParams({ actingBodyId, includeDeleted })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    /**
     *
     * @param id
     * @throws {ClientError} CantFinishConcludedSituation
     */
    async finishOccurrence(id: number, category: number, subCategory: number, other?: string) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence/${id}/finish`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ category, subCategory, other })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async bulkFinishOccurrences({ actingBodyId, beginIn, endIn, triggerType, finishCategoryId, finishSubCategoryId }: BulkFinishOccurrencesParams): Promise<{ count: number; }> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrences/bulk-finish`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                actingBodyId,
                beginIn,
                endIn,
                triggerType,
                finishReason: {
                    category: finishCategoryId,
                    subCategory: finishSubCategoryId
                }
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    /**
     *
     * @param id
     * @throws {ClientError} CantReopenOpenedSituation
     * @throws {ClientError} MoreThan72Hours
     */
    async reopenOccurrence(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence/${id}/reopen`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async createReport(params: CreateReportParameters) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/report`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 204) {
            return [];
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getReports(params: GetResourcePaginatedByActingBodyParameters): Promise<PaginatedResource<ReportOccurrence>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/reports/paginated${this.encodeQueryParams({
            ...params,
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getReport(id: number): Promise<{ resultUrl: string; }> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/report/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getAgents({ actingBodyId }: {
        actingBodyId?: number;
    }): Promise<SimplifiedUserForUnit[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/agents${this.encodeQueryParams({ actingBodyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getEventOperators({ actingBodyId, occurrenceId }: {
        actingBodyId: number;
        occurrenceId: number;
    }): Promise<{ id: number; name: string; warName: string; }[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/event-operators${this.encodeQueryParams({ actingBodyId, occurrenceId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    /**
     *
     * @param occurrenceId
     * @param params
     * @returns
     * @throws {AlreadyWithThisUser}
     * @throws {ClientError} CantEditConcludedSituation
     */
    async transferOccurrence(occurrenceId: number, params: {
        eventOperatorId: number;
    }): Promise<OccurrenceReport> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence/${occurrenceId}/transfer`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });
        if (res.status == 403) {
            throw new ClientError('ForbiddenToTransferOccurrence');
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'AlreadyWithThisUser') {
                throw new AlreadyWithThisUser();
            }
            throw new ClientError(resJson.code);
        }
        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getAssignments(params: GetResourcePaginatedByActingBodyParameters): Promise<PaginatedResource<Assignment>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/assignments${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedAssignments(actingBodyId?: number): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/assignments-simplified${this.encodeQueryParams({ actingBodyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getAssignment(id: number): Promise<Assignment> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/assignment/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createAssignment({ name, description, actingBodyId }: CreateAssignmentParameters): Promise<Assignment> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/assignment`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ name, description, actingBodyId }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 400) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateAssignment({ id, name, description }: PartialBy<Omit<BaseAssignment, 'actingBodyId'>, 'id'>): Promise<Assignment> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/assignment/${id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ name, description }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 400) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteAssignment(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/assignment/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async createOccurrence(occurrence: {
        phone: string;
        requester: string;
        latitude: number;
        longitude: number;
        priority: OccurrencePriorities;
        narrative: string;
        occurrenceTypeId: number;
        tags?: number[];
        involvedVehicles?: InvolvedVehicle[];
        involvedPeople?: InvolvedPerson[];
        involvedObjects?: InvolvedObject[];
        actingBodyId?: number;
        cityPropertyId?: number | null;
        policeReport: string;
        unassignedOccurrence?: boolean;
    }): Promise<Occurrence> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(occurrence)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    /**
     *
     * @param occurrence
     * @returns
     * @throws {ClientError} CantEditConcludedSituation
     */
    async updateOccurrence(occurrence: {
        id: number;
        updatedAt: string;
        phone?: string;
        requester?: string;
        latitude?: number;
        longitude?: number;
        priority?: OccurrencePriorities;
        narrative?: string;
        occurrenceTypeId?: number;
        unitsToSet?: {
            id: number;
            isStarter: boolean;
            displaced: boolean;
            displaceReasonId?: number;
            policeStationId?: number;
        }[];
        tags?: number[];
        involvedVehicles?: InvolvedVehicle[];
        involvedPeople?: InvolvedPerson[];
        involvedObjects?: InvolvedObject[];
        cityPropertyId?: number | null;
        policeReport: string;
    }): Promise<Occurrence> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence/${occurrence.id}`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(occurrence)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            } else if (resJson.code == 'RecordNotLatestValues') {
                throw new RecordNotLatestValues(resJson.code, resJson.userName);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOccurrence(id: number): Promise<Occurrence> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSignableReport(signableReportId: number): Promise<{ resultUrl: string; }> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/signable-report/${signableReportId}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async generatePDFOccurrence(id: number, data: Record<string, string>) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence/${id}/generate-pdf`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                translate: data,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        const blob = res.blob();

        return blob;
    }

    /**
     *
     * @param id
     * @throws {ClientError}
     */
    async setOccurrenceInService(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence/${id}/in_service`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getStatistics(actingBodyId?: number): Promise<Statistic> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/statistics${this.encodeQueryParams({
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            actingBodyId: actingBodyId
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOccurrencePriorities(): Promise<string[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence_priorities`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 204) {
            return [];
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOccurrenceType(id: number): Promise<OccurrenceType> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence_type/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteOccurrenceType(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence_type/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async createOccurrenceType(occurrenceType: CreateOccurrenceType): Promise<OccurrenceType> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence_type`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(occurrenceType)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateOccurrenceType(occurrenceType: PartialBy<Omit<BaseOccurrenceType, 'actingBodyId'>, 'id'>): Promise<OccurrenceType> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence_type/${occurrenceType.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(occurrenceType)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getVehicleTypes(): Promise<string[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/vehicle_type`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 204) {
            return [];
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getVehicleStatus(): Promise<VehicleStatus[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/vehicle_status`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 204) {
            return [];
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createEquipment(equipment: CreateEquipment): Promise<Equipment> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/equipment`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(equipment)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateEquipment(equipment: PartialBy<Omit<BaseEquipment, 'actingBodyId'>, 'id'>): Promise<Equipment> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/equipment/${equipment.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(equipment)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteEquipment(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/equipment/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async createOccurrenceAttachment(occurrenceId: number, file: File, description?: string): Promise<OccurrenceReport> {
        const token = await this.getToken();
        const formData = new FormData();
        formData.append('file', file, file.name);

        if (description) {
            formData.append('description', description);
        }

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence/${occurrenceId}/attachment`, {
            method: 'POST',
            headers: this.getHeaders(token),
            body: formData,
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createOccurrenceReport(occurrenceId: number, content: string): Promise<OccurrenceReport> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence/${occurrenceId}/report`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ 'content': content }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getCaptureHistoric(occurrenceId: number): Promise<Occurrence[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrence/${occurrenceId}/capture-historic`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPaginatedUnits(params: GetUnitsPaginatedParameters): Promise<PaginatedResource<PaginatedUnit>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/units/paginated${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getUnits(params: { equipmentsIds: number[]; skillsIds: number[]; unitName?: string; dispatchedUnits: number[]; actingBodyUnitIds?: number[]; actingBodyId: number; viewAll: boolean; }): Promise<Unit[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/units${this.encodeQueryParams(params)}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOccurrenceDispatchedUnits(occurrenceId: number): Promise<Unit[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/${occurrenceId}/dispatched-units`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedUnits(): Promise<AppAgentUnit[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/units-simplified`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createUnit(params: CreateUnitParameters): Promise<Unit> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/unit`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code === 'EquipmentAlreadyInUse') {
                throw new EquipmentAlreadyInUse();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateUnit(params: UpdateUnitParameters): Promise<Unit> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit/${params.unitId}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code === 'EquipmentAlreadyInUse') {
                throw new EquipmentAlreadyInUse();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deactivateAgent(unitId: number, agentId: number, finishEarlyReasonId: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit/${unitId}/deactivate-agent`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ agentId, finishEarlyReasonId }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    /**
     *
     * @returns
     * @throws {ClientError} CannotFinishUnitWithOccurrences
     */
    async finishUnit(id: number, finishEarlyReasonId: number): Promise<void> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/unit/${id}/finished`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ finishEarlyReasonId })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    /**
     *
     * @returns
     * @throws {ClientError} CurrentUnitNotActive | CannotPauseUnitWithOccurrences
     */
    async pauseUnpauseUnit(id: number, action: 'pause' | 'unpause', pauseReasonId?: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/unit/${id}/pause`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ action, pauseReasonId })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getPauseReasons(unitId: number): Promise<PauseReason[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit/pause-reasons${this.encodeQueryParams({ unitId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getDisplaceReasons(): Promise<DisplaceReason[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit/displace-reasons`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPoliceStations(): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit/police-stations-simplified`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getUnit(id: number): Promise<Unit> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getUnitHistoric(id: number): Promise<Log[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit/${id}/finished-historic`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOccurrenceTags(params: GetResourcePaginatedByActingBodyParameters): Promise<PaginatedResource<OccurrenceTag>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrences/paginated-tags${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getDropDownCameraTags(): Promise<{ id: number; name: string; color: string; }[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/simplified-camera-tags`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createOccurrenceTag(params: CreateOccurrenceTag): Promise<OccurrenceTag> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrences/tag`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateOccurrenceTag(params: PartialBy<Omit<BaseOccurrenceTag, 'actingBodyId'>, 'id'>): Promise<OccurrenceTag> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrences/tag/${params.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getDropDownOccurrenceTags(actingBodyId?: number): Promise<SimplifiedOccurrenceTag[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/simplified-occurrence-tags${this.encodeQueryParams({
            actingBodyId
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteOccurrenceTag(id: number): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/occurrences/tag/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getDropDownReportTags(actingBodyId?: number): Promise<SimplifiedReportTag[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/simplified-report-tags${this.encodeQueryParams({
            actingBodyId
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getReportTags(params: GetResourcePaginatedByActingBodyParameters): Promise<PaginatedResource<ReportTag>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/reports/paginated-tags${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createReportTag(params: CreateOccurrenceTag): Promise<ReportTag> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/reports/tag`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateReportTag(params: PartialBy<Omit<BaseOccurrenceTag, 'actingBodyId'>, 'id'>): Promise<ReportTag> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/reports/tag/${params.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteReportTag(id: number): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/reports/tag/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getPaginatedObjectsCategories(params: GetResourcePaginatedByActingBodyParameters): Promise<PaginatedResource<ObjectCategory>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/objects-categories${this.encodeQueryParams({
            limit: params.limit,
            page: params.page,
            textFilter: params.textFilter,
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedObjectsCategories(actingBodyId: number): Promise<ObjectCategory[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/objects-categories-simplified${this.encodeQueryParams({ actingBodyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedFinishReasons(actingBodyId: number): Promise<FinishCategoryBase[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/finish-reasons-simplified${this.encodeQueryParams({ actingBodyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedObjectSubCategories(id: number): Promise<{ id: number; name: string; }[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/object-sub-categories-simplified/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }
        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedSubCategoriesFinishReason(finishCategoryId: number): Promise<{ id: number; name: string; }[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/finish-subcategories${this.encodeQueryParams({ finishCategoryId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }
        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteObjectCategory(id: number): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/object-category/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async createObjectCategory(params: CreateObjectCategory): Promise<ObjectCategory> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/object-category`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateObjectCategory(params: PartialBy<ObjectCategory, 'id'>): Promise<ObjectCategory> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/object-category/${params.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getObjectCategory(id: number): Promise<ObjectCategory> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/object-category/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteObjectSubCategory(id: number): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/object-sub-category/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getTeamNames(params: GetResourcePaginatedByActingBodyParameters): Promise<PaginatedResource<TeamName>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/unit-names${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getTeamName(id: number): Promise<TeamName> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/unit-name/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getTeamsFromTeamName(id: number, params: { startDate: number; endDate: number; }): Promise<{ id: number; start: number; finish: number; }[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/unit-name/${id}/units${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteTeamName(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/unit-name/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async createTeamName(teamName: CreateTeamName): Promise<TeamName> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/unit-name`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(teamName)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateTeamName(teamName: PartialBy<Omit<BaseTeamName, 'actingBodyId'>, 'id'>): Promise<TeamName> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/unit-name/${teamName.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(teamName)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteFinishCategory(id: number): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/finish-category/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async createFinishCategory(params: { name: string; finishSubCategories?: Partial<FinishSubCategory>[]; actingBodyId?: number; }): Promise<FinishCategory> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/finish-category`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateFinishCategory(id: number, params: { name: string; finishSubCategories?: Partial<FinishSubCategory>[]; }): Promise<FinishCategory> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/finish-category/${id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getFinishCategory(id: number): Promise<FinishCategory> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/finish-category/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPaginatedFinishCategories(params: GetResourcePaginatedByActingBodyParameters): Promise<PaginatedResource<FinishCategory>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/finish-categories${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteFinishSubCategory(id: number): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/finish-sub-category/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getPaginatedUnitPrograms(params: GetResourcePaginatedByActingBodyParameters): Promise<PaginatedResource<UnitProgram>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/unit-programs/paginated${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createUnitProgram(unitProgram: CreateUnitProgram): Promise<UnitProgram> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit-programs`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(unitProgram)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateUnitProgram(unitProgram: PartialBy<Omit<BaseUnitProgram, 'actingBodyId'>, 'id'>): Promise<UnitProgram> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit-programs/${unitProgram.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(unitProgram)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteUnitProgram(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit-programs/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getPaginatedPoliceStations(params: GenericPaginatedParameters): Promise<PaginatedResource<PoliceStation>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/police-stations/paginated${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPoliceStationsPositions(): Promise<PoliceStation[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/police-stations/positions`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPoliceStation(id: number): Promise<PoliceStation> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/police-stations/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createPoliceStation(policeStation: CreatePoliceStation): Promise<PoliceStation> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/police-stations`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(policeStation)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updatePoliceStation(policeStation: PoliceStation): Promise<PoliceStation> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/police-stations/${policeStation.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(policeStation)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deletePoliceStation(id: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/police-stations/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getPaginatedProvidences(params: GenericPaginatedParameters): Promise<PaginatedResource<Providence>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/providences/paginated${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createProvidence(providence: CreateProvidence): Promise<Providence> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/providence`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(providence)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateProvidence(payload: { id: number; name: string; actingBodyId?: number; }) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/providence/${payload.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ name: payload.name, actingBodyId: payload.actingBodyId }),
        });

        if (res.status === 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status < 500) {
            const { code } = await res.json();
            throw new ClientError(code);
        }

        if (res.status !== 200) {
            throw new UnexpectedError();
        }

        return res.json() as Promise<Providence>;
    }

    async deleteProvidence(id: number): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/providence/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getPaginatedUnitPauseReasons(params: GenericPaginatedParameters): Promise<PaginatedResource<UnitPauseReason>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/unit-pause-reasons/paginated${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createUnitPauseReason(unitPauseReason: CreateUnitPauseReason): Promise<UnitPauseReason> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/unit-pause-reasons`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(unitPauseReason)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateUnitPauseReason(unitPauseReason: PartialBy<Omit<BaseUnitPauseReason, 'actingBodyId'>, 'id'>): Promise<UnitPauseReason> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/unit-pause-reasons/${unitPauseReason.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(unitPauseReason)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteUnitPauseReason(id: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/unit-pause-reasons/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getPaginatedUnitDisplacedReasons(params: GetResourcePaginatedByActingBodyParameters): Promise<PaginatedResource<UnitDisplacedReason>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/unit/displaced-reasons/paginated${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status === 401) {
            throw new Unauthorized();
        }

        if (res.status !== 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getUnitDisplacedReasonById(id: number): Promise<UnitDisplacedReason> {
        const token = await this.getToken();
        const url = `${this.centralEndpoint}/dispatch/unit/displaced-reasons/${id}`;
        const res = await fetch(url, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status === 401) {
            throw new Unauthorized();
        }
        if (res.status === 404) {
            throw new NotFound();
        }
        if (res.status !== 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async createUnitDisplacedReason(payload: CreateUnitDisplacedReason): Promise<UnitDisplacedReason> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit/displaced-reasons`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(payload),
        });

        if (res.status === 401) {
            throw new Unauthorized();
        }

        if (res.status !== 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateUnitDisplacedReason(id: number, params: { name: string; unitDisplacedReason?: Partial<UnitDisplacedReason>[]; }): Promise<UnitDisplacedReason> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit/displaced-reasons/${id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status === 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status < 500) {
            const { code } = await res.json();
            throw new ClientError(code);
        }

        if (res.status !== 200) {
            throw new UnexpectedError();
        }

        return res.json() as Promise<UnitDisplacedReason>;
    }

    async deleteUnitDisplacedReason(id: number): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit/displaced-reasons/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status === 401) {
            throw new Unauthorized();
        }

        if (res.status !== 204) {
            throw new UnexpectedError();
        }
    }

    async getPaginatedAgentsSchedules(params: GetResourcePaginatedByActingBodyParameters): Promise<PaginatedResource<WorkSchedule>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/agents-schedules/paginated${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getCityProperty(id: number): Promise<CityProperty> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/city-properties/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPaginatedCityProperties(params: GenericPaginatedParameters): Promise<PaginatedResource<CityProperty>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/city-properties/paginated${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createCityProperty(CityProperty: CreateCityProperty): Promise<CityProperty> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/city-properties`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(CityProperty)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'AlreadyExists') {
                throw new AlreadyExists('CityPropertyAlreadyExists');
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateCityProperty(CityProperty: PartialBy<CityProperty, 'id'>): Promise<CityProperty> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/city-properties/${CityProperty.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(CityProperty)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteCityProperty(id: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/city-properties/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getPaginatedHandcuffReasons(params: GenericPaginatedParameters): Promise<PaginatedResource<HandcuffReason>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/handcuff-reasons/paginated${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createHandcuffReason(handcuffReason: CreateHandcuffReason): Promise<HandcuffReason> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/handcuff-reasons`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(handcuffReason)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'AlreadyExists') {
                throw new AlreadyExists('HandcuffReasonAlreadyExists');
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateHandcuffReason(handcuffReason: CreateHandcuffReasonParameters): Promise<HandcuffReason> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/handcuff-reasons/${handcuffReason.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(handcuffReason)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteHandcuffReason(id: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/handcuff-reasons/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getPaginatedUnitFinishEarlyReasons(params: GenericPaginatedParameters): Promise<PaginatedResource<UnitFinishEarlyReason>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/unit-finish-early-reasons/paginated${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createUnitFinishEarlyReason(unitFinishEarlyReason: Omit<BaseUnitFinishEarlyReason, 'id'>): Promise<UnitFinishEarlyReason> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/unit-finish-early-reasons`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(unitFinishEarlyReason)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateUnitFinishEarlyReason(unitFinishEarlyReason: PartialBy<BaseUnitFinishEarlyReason, 'id'>): Promise<UnitFinishEarlyReason> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/unit-finish-early-reasons/${unitFinishEarlyReason.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(unitFinishEarlyReason)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteUnitFinishEarlyReason(id: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dispatch/unit-finish-early-reasons/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getFinishEarlyReasons(unitId: number): Promise<FinishEarlySimplified[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit/finish-early-reasons${this.encodeQueryParams({ unitId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPatrolIndicators(params: GetResourcePaginatedByActingBodyParameters): Promise<PaginatedResource<PatrolIndicator>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/patrol-indicators${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPatrolIndicatorById(id: number): Promise<PatrolIndicator> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/patrol-indicator/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createPatrolIndicator(params: CreatePatrolIndicator): Promise<PatrolIndicator> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/patrol-indicator`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updatePatrolIndicator(params: UpdatePatrolIndicator): Promise<PatrolIndicator> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/patrol-indicator/${params.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deletePatrolIndicator(id: number): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/patrol-indicator/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getActivitiesAndServicesForUnit(unitId: number): Promise<ActivityAndService[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit/${unitId}/activities-and-services`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPolicePatrolSchedulesForUnit(unitId: number): Promise<PolicePatrolSchedule[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/unit/${unitId}/police-patrol-schedules`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    // PATROL ACTIVITIES
    async getPatrolActivities(params: GetResourcePaginatedByActingBodyParameters): Promise<PaginatedResource<PatrolActivity>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/patrol-activities${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPatrolActivityById(id: number): Promise<PatrolActivity> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/patrol-activity/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createPatrolActivity(params: CreatePatrolActivity): Promise<PatrolActivity> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/patrol-activity`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updatePatrolActivity(params: PartialBy<UpdatePatrolActivity, 'id'>): Promise<PatrolActivity> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/patrol-activity/${params.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deletePatrolActivity(id: number): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dispatch/patrol-activity/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }
}

export const dispatchService = new DispatchService();
